import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { wireQuotationsAPI } from '../../services/api';
import { printStyles, CompanyLetterhead } from './shared';
import {
  WIRE_COLUMNS, DEFAULT_PROFILES, WIRE_MANDATORY_IDS,
  autoHideEmptyWireColumns, getFontConfig, getColumnWidths, getVisibleColumns, getItemValue,
} from '../../config/tableColumns';
import { computeLineItemTotals, formatNumberTotal } from '../../utils/lineItemTotals';

export function WireQuotationPrintView() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printTriggered, setPrintTriggered] = useState(false);
  const containerRef = useRef(null);
  const isPreviewMode = new URLSearchParams(window.location.search).get('preview') === 'true';

  useEffect(() => {
    wireQuotationsAPI.get(id)
      .then((r) => { setQ(r.data); setLoading(false); })
      .catch(() => { setError('Failed to load wire quotation'); setLoading(false); });
  }, [id]);

  useEffect(() => {
    if (!loading && q && !printTriggered && !isPreviewMode) {
      setPrintTriggered(true);
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [loading, q, printTriggered, isPreviewMode]);

  const formatCurrency = (amount, currency = 'INR') => {
    const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£', JPY: '¥', AED: 'AED ', SGD: 'S$' };
    return `${symbols[currency] || currency + ' '}${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const formatCurrency4 = (amount, currency = 'INR') => {
    const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£', JPY: '¥', AED: 'AED ', SGD: 'S$' };
    return `${symbols[currency] || currency + ' '}${(amount || 0).toFixed(4)}`;
  };
  const formatDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'white' }}>
        <style>{printStyles}</style>
        <div className="spinner" style={{ marginBottom: '20px' }} />
        <p style={{ color: '#1e40af', fontSize: '18px', fontWeight: '600' }}>Generating Wire Quotation…</p>
      </div>
    );
  }
  if (error || !q) {
    return (
      <div className="print-container">
        <style>{printStyles}</style>
        <p style={{ color: 'red', textAlign: 'center' }}>{error || 'Quotation not found'}</p>
      </div>
    );
  }

  const currency = q.currency || 'INR';
  const items = q.items || [];
  let selectedIds = q.pdf_columns || DEFAULT_PROFILES.wire_quotation;
  const hideEmpty = q.auto_hide_empty !== false;
  if (hideEmpty) selectedIds = autoHideEmptyWireColumns(items, selectedIds);
  const visibleCols = getVisibleColumns(selectedIds, WIRE_COLUMNS);
  const widths = getColumnWidths(selectedIds, WIRE_COLUMNS);
  const font = getFontConfig(visibleCols.length);

  const renderCellValue = (item, colId, idx) => {
    const priceUnit = item.price_unit || 1;
    const perLabel = priceUnit === 1 ? 'Kg' : 'MT';
    switch (colId) {
      case 'row_num':       return idx + 1;
      case 'part_no':       return item.part_internal_no || '-';
      case 'description':   return item.description || '-';
      case 'wire_diameter': return getItemValue(item, 'wire_diameter') || '-';
      case 'wire_grade':    return item.wire_grade || '-';
      case 'uts_notes':     return item.uts_notes || '-';
      case 'hs_code':       return item.hs_code || '-';
      case 'coil_weight':   return item.coil_weight_kg ? `${Number(item.coil_weight_kg).toLocaleString('en-IN')}` : '-';
      case 'no_of_coils':   return item.no_of_coils ? Number(item.no_of_coils).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '-';
      case 'total_kg':      return Number(item.total_kg || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
      case 'price':         return `${formatCurrency4(item.unit_price, currency)} / ${perLabel}`;
      case 'total':         return formatCurrency(item.line_total, currency);
      default:              return '-';
    }
  };

  const cellStyle = (col) => {
    const base = { textAlign: col.align, verticalAlign: 'top', padding: font.padding, wordBreak: 'break-word', overflowWrap: 'anywhere' };
    if (col.id === 'part_no') return { ...base, fontWeight: '600', fontFamily: 'monospace', whiteSpace: 'normal' };
    if (col.id === 'description' || col.id === 'uts_notes') return { ...base, whiteSpace: 'normal' };
    return base;
  };

  const subtotal = items.reduce((s, it) => s + (it.line_total || 0), 0);
  const wireTotals = computeLineItemTotals(items);

  return (
    <div className="print-container landscape" ref={containerRef}>
      <style>{printStyles}</style>
      <style>{`@media print { @page { size: A4 landscape; margin: 8mm; } }`}</style>

      {!isPreviewMode && (
        <button className="download-pdf-btn no-print" onClick={() => window.print()} data-testid="wire-quotation-preview-pdf-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 6 2 18 2 18 9"/>
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
            <rect x="6" y="14" width="12" height="8"/>
          </svg>
          Print / Save as PDF
        </button>
      )}

      <CompanyLetterhead
        documentType="WIRE QUOTATION"
        documentNumber={q.quotation_number}
        revisionNumber={q.revision_number}
      />

      <div className="info-grid">
        <div className="info-box">
          <h4>Customer Details</h4>
          <p><span className="value">{q.customer_name}</span></p>
          {q.customer_code && <p>Code: {q.customer_code}</p>}
          {q.customer_country && <p>Country: {q.customer_country}</p>}
          {q.customer_contact_person && <p>Contact: {q.customer_contact_person}</p>}
        </div>
        <div className="info-box">
          <h4>Quotation Details</h4>
          <p>Date: <span className="value">{formatDate(q.created_at)}</span></p>
          <p>Valid Until: <span className="value">{q.validity_date ? formatDate(q.validity_date) : `${q.validity_days || 30} days`}</span></p>
          <p>Currency: <span className="value">{currency}</span></p>
          {q.delivery_terms && <p>Delivery Terms: <span className="value">{q.delivery_terms}</span></p>}
          {q.payment_terms && <p>Payment Terms: <span className="value">{q.payment_terms}</span></p>}
          {q.incoterms && <p>Incoterms: <span className="value">{q.incoterms}</span></p>}
        </div>
      </div>

      <table className="items-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px', fontSize: font.body }}>
        <thead>
          <tr style={{ background: '#1e40af', color: '#fff' }}>
            {visibleCols.map((col) => (
              <th key={col.id} style={{ padding: font.padding, fontSize: font.header, textAlign: col.align, width: widths[col.id] }}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={it.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
              {visibleCols.map((col) => (
                <td key={col.id} style={cellStyle(col)}>{renderCellValue(it, col.id, idx)}</td>
              ))}
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={visibleCols.length} style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>No line items</td></tr>
          )}
        </tbody>
        <tfoot>
          <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
            {visibleCols.map((col) => (
              <td key={col.id} style={{ ...cellStyle(col), borderTop: '2px solid #1e40af' }}>
                {col.id === 'description' ? `TOTAL (${wireTotals.count} item${wireTotals.count === 1 ? '' : 's'})`
                  : col.id === 'no_of_coils' ? formatNumberTotal(wireTotals.coils)
                  : col.id === 'total_kg' ? formatNumberTotal(wireTotals.qtyKg || wireTotals.totalWeightKg)
                  : col.id === 'total' ? formatCurrency(subtotal, currency)
                  : ''}
              </td>
            ))}
          </tr>
          <tr style={{ background: '#1e40af', color: '#fff', fontWeight: 700 }}>
            <td colSpan={visibleCols.length - 1} style={{ textAlign: 'right', padding: font.padding }}>Total</td>
            <td style={{ textAlign: 'right', padding: font.padding }}>{formatCurrency(q.total_amount || subtotal, currency)}</td>
          </tr>
        </tfoot>
      </table>

      {q.notes && (
        <div style={{ marginTop: '12px', fontSize: '11px', color: '#475569' }}>
          <strong>Notes:</strong> {q.notes}
        </div>
      )}
      {q.terms_conditions && (
        <div style={{ marginTop: '8px', fontSize: '10px', color: '#475569', whiteSpace: 'pre-wrap' }}>
          <strong>Terms & Conditions:</strong>
          <div>{q.terms_conditions}</div>
        </div>
      )}
    </div>
  );
}

export default WireQuotationPrintView;
